import { addDays, endOfMonth } from 'date-fns'
import { getActiveDaysArray, parseServiceIntervalData } from './dataTransforms'
import { generateRecurrenceEvents, generateNonWeeklyEvents } from './eventGeneration'

/**
 * Build events for given recurrence parameters
 * @param {Object} params - The recurrence parameters object
 * @param {string} params.frequency - Frequency string (number of times per interval)
 * @param {string|Object} params.serviceIntervalData - Service interval string or backend recurrence object
 * @param {Object} params.selectedDays - daysStructure with day objects containing isSelected property (for weekly recurrence)
 * @param {Object} params.existingEvents - Existing events to consider for avoiding duplicates
 * @param {Object} params.serviceEventData - Service event data for additional context
 * @param {Date} [params.startDate=new Date()] - Start date to use as reference
 * @param {number} [params.horizonDays=90] - Number of days in the future to generate events
 * @param {Object} [params.nonWeeklyParams={}] - Non-weekly recurrence parameters
 * @returns {Array} Array of event objects
 */
export function buildRecurrenceEvents({
  frequency,
  serviceIntervalData,
  selectedDays,
  existingEvents,
  serviceEventData,
  startDate = new Date(),
  horizonDays = 90,
  nonWeeklyParams = {},
}) {
  const endDate = endOfMonth(addDays(startDate, horizonDays))
  const { recurrenceInterval, recurrencePer } = parseServiceIntervalData(serviceIntervalData)
  const intervalLower = recurrenceInterval.toLowerCase()

  // Get active days and apply frequency limit
  const activeDays = getActiveDaysArray(selectedDays)
  const frequencyLimit = parseInt(frequency, 10) || 1
  const limitedActiveDays = intervalLower === 'day' ? [0] : activeDays.slice(0, frequencyLimit) // placeholder for daily

  // Only return early if no active days AND not using non-weekly parameters with eventInstanceType
  if (limitedActiveDays.length === 0 && intervalLower !== 'day' && !(intervalLower !== 'week' && nonWeeklyParams.eventInstanceType)) {
    return []
  }

  // Daily uses the simple every-N-days generator; only monthly/quarterly/yearly need the specialized path.
  if (intervalLower !== 'week' && intervalLower !== 'day' && nonWeeklyParams.eventInstanceType) {
    const dayOfWeekNumbers = Array.isArray(nonWeeklyParams.dayOfTheWeek)
      ? nonWeeklyParams.dayOfTheWeek.map(
          day => ({ Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 }[day] ?? -1)
        )
      : [{ Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 }[nonWeeklyParams.dayOfTheWeek] ?? -1]

    return generateNonWeeklyEvents(
      intervalLower,
      recurrencePer,
      startDate,
      endDate,
      {
        eventInstanceType: nonWeeklyParams.eventInstanceType,
        dayOfMonth: nonWeeklyParams.dayOfTheMonth,
        dayOfWeek: dayOfWeekNumbers,
        occurrence: nonWeeklyParams.eventOccurrence,
      },
      serviceEventData,
      existingEvents
    ).sort((a, b) => new Date(a.serviceDate) - new Date(b.serviceDate))
  }

  // Use the consolidated event generator for standard recurrence patterns
  const events = generateRecurrenceEvents(
    intervalLower,
    recurrencePer,
    startDate,
    endDate,
    limitedActiveDays,
    serviceEventData,
    existingEvents
  )
  return events.sort((a, b) => new Date(a.serviceDate) - new Date(b.serviceDate))
}
